import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Chip,
  Divider,
  TextareaAutosize,
} from '@mui/material';
import { ChangeEvent, FC, useEffect } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import {
  API,
  Scheduling,
  SendEmail,
  SendSMS,
  TakingMessage,
  TransferCall,
} from './CallActions';
import { useSelector, useDispatch } from 'react-redux';
import { ValidationError } from 'yup';
import { RootState } from '../../utils/redux/redux';
import {
  updatePersonalization,
  setStepStatus,
  setErrors,
  clearErrors,
  selectedActionData,
} from '../../utils/redux/slices/userJourneySlice';
import { personalizeSchema } from '../../schemas/userJourney/personalize';
import theme from '../../theme';

interface PersonalizationProps {
  selectedSubSection: string;
  onNext: () => void;
  onPrev: () => void;
  isPending: boolean;
}

const Personalization: FC<PersonalizationProps> = ({
  selectedSubSection,
  onNext,
  onPrev,
  isPending,
}) => {
  const dispatch = useDispatch();
  const { personalization, errors } = useSelector(
    (state: RootState) => state.userJourney
  );

  useEffect(() => {
    dispatch(
      updatePersonalization({
        identity: `You are a helpful {Bot_Name} AI assistant participating in a phone call for a {Restaurant_Name}. Your goal is to demonstrate your capabilities in a succinct way. Your responses will be converted to audio, so do not use special characters, bold formatting, emojis, or any visual emphasis`,
      })
    );
  }, [dispatch]);

  // const { agents } = useAgents();

  // useEffect(() => {
  //   if (
  //     agents.agents[0]?.file?.firstMessage &&
  //     agents.agents[0]?.file?.identity &&
  //     agents.agents[0]?.file?.job
  //   ) {
  //     dispatch(
  //       updatePersonalization({
  //         greeting: agents.agents[0]?.file?.firstMessage,
  //         identity: agents.agents[0]?.file?.identity,
  //         job: agents.agents[0]?.file?.job,
  //       })
  //     );
  //   }
  // }, [agents, dispatch]);

  useEffect(() => {
    const hasRequiredFields =
      personalization.data.greeting &&
      personalization.data.identity &&
      personalization.data.job;

    if (hasRequiredFields) {
      dispatch(
        setStepStatus({
          step: 'personalization',
          status: { isComplete: true, hasError: false },
        })
      );
    }
  }, [personalization.data, dispatch]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    dispatch(updatePersonalization({ [name]: value }));

    if (errors[name]) {
      dispatch(setErrors({ ...errors, [name]: '' }));
    }

    const updatedData = {
      ...personalization.data,
      [name]: value,
    };

    const hasRequiredFields =
      updatedData.greeting && updatedData.identity && updatedData.job;

    dispatch(
      setStepStatus({
        step: 'personalization',
        status: {
          isComplete: hasRequiredFields ? true : false,
          hasError: false,
        },
      })
    );
  };
  const handleActionAdd = (value: selectedActionData) => {
    if (value && !personalization.data?.selectedActions?.includes(value)) {
      dispatch(
        updatePersonalization({
          selectedActions: [
            ...(personalization.data?.selectedActions || []),
            value,
          ],
        })
      );
    }
  };

  const handleActionDelete = (actionToDelete: string) => {
    dispatch(
      updatePersonalization({
        selectedActions: personalization.data.selectedActions?.filter(
          action => action !== actionToDelete
        ),
      })
    );
  };

  const handleNext = async () => {
    try {
      await personalizeSchema.validate(personalization.data, {
        abortEarly: false,
      });
      dispatch(
        setStepStatus({
          step: 'personalization',
          status: { isComplete: true, hasError: false },
        })
      );
      dispatch(clearErrors());
      onNext();
    } catch (err) {
      if (err instanceof ValidationError) {
        const newErrors: Record<string, string> = {};
        err.inner.forEach(error => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        dispatch(setErrors(newErrors));
        dispatch(
          setStepStatus({
            step: 'personalization',
            status: { isComplete: false, hasError: true },
          })
        );
      }
    }
  };

  const handlePrev = () => {
    onPrev();
  };

  const getSectionStyle = (sectionId: string) => {
    if (selectedSubSection === sectionId) {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    return {
      transform: selectedSubSection === sectionId ? 'scale(1.02)' : 'scale(1)',
      transition: 'transform 0.2s ease',
    };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'white',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          px: 3,
          py: 2,
          height: 'calc(100vh - 100px)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          backgroundColor: 'white',
          width: '768px',
          mx: 'auto',
        }}
      >
        {/* Header Section */}
        <div>
          <Typography
            sx={{ fontWeight: 600, fontSize: '18px', color: 'custom.textBlue' }}
          >
            Customize Your AI's Responses
          </Typography>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 400, color: 'lightGray.text' }}
          >
            We've pre-filled responses based on your selected template, but you
            can tweak them to match your brand's voice
          </Typography>
        </div>
        <Divider />

        {/* Greeting Message */}
        <Box
          id="greeting"
          sx={{
            ...getSectionStyle('greeting'),
            display: 'flex',
            gap: 1,
            py: 3,
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                color: 'custom.textBlue',
                fontSize: '16px',
              }}
            >
              Greeting Message
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: '12px', width: '300px' }}
            >
              Specify how your AI will initiate conversations with customers.{' '}
              <br /> Example Hi, this is [Agent Name], your AI assistant for
              [Restaurant Name]. How can I assist you today?
            </Typography>
          </Box>
          <TextareaAutosize
            name="greeting"
            value={personalization.data.greeting}
            onChange={handleChange}
            placeholder="Example: Hi, I'm [Agent Name], your AI assistant for [Restaurant Name]. How can I assist you today?"
            minRows={5}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: errors.greeting
                ? '1px solid #d32f2f'
                : '1px solid #DCE0E7',
              borderRadius: '6px',
              fontFamily: 'inherit',
              resize: 'none',
              outline: 'none',
              backgroundColor: 'white',
            }}
          />
        </Box>
        {errors.greeting && (
          <Typography
            variant="caption"
            color="error"
            sx={{ ml: 'auto', mr: 0 }}
          >
            {errors.greeting}
          </Typography>
        )}
        <Divider />

        {/* Assistant Identity */}
        <Box
          id="identity"
          sx={{
            ...getSectionStyle('identity'),
            gap: 1,
            py: 3,
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Agent Identity
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: '12px' }}
            >
              Give your AI a personality that matches your brand. <br /> Example
              I'm Quibble, your friendly AI assistant, here to make ordering
              easy!
            </Typography>
          </Box>
          <TextareaAutosize
            name="identity"
            value={personalization.data.identity}
            onChange={handleChange}
            placeholder="Example: I'm [Guide, your friendly AI assistant here]"
            minRows={5}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: errors.identity
                ? '1px solid #d32f2f'
                : '1px solid #DCE0E7',
              borderRadius: '6px',
              fontFamily: 'inherit',
              resize: 'none',
              outline: 'none',
              backgroundColor: 'white',
            }}
          />
        </Box>
        {errors.identity && (
          <Typography
            variant="caption"
            color="error"
            sx={{ ml: 'auto', mr: 0 }}
          >
            {errors.identity}
          </Typography>
        )}
        <Divider />

        {/* Agent Job */}
        <Box
          id="job"
          sx={{ ...getSectionStyle('job'), display: 'flex', gap: 1, py: 3 }}
        >
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Agent Job
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: '12px', width: '300px' }}
            >
              Set clear expectations for what your AI assistant will handle.{' '}
              <br /> Example "I can help you place an order, check availability,
              and answer common questions!"
            </Typography>
          </Box>
          <TextareaAutosize
            name="job"
            value={personalization.data.job}
            onChange={handleChange}
            placeholder="Example: I can help you place an order, check availability and answer common questions!"
            minRows={5}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: errors.job ? '1px solid #d32f2f' : '1px solid #DCE0E7',
              borderRadius: '6px',
              fontFamily: 'inherit',
              resize: 'none',
              outline: 'none',
              backgroundColor: 'white',
            }}
          />
        </Box>
        {errors.job && (
          <Typography
            variant="caption"
            color="error"
            sx={{ ml: 'auto', mr: 0 }}
          >
            {errors.job}
          </Typography>
        )}
        <Divider />

        {/* Call Action */}
        <Box
          id="call-action"
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Box
            sx={{
              ...getSectionStyle('call-action'),
              display: 'flex',
              gap: 1,
              py: 3,
              alignItems: 'center',
            }}
          >
            <Box
              sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Call Action
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontSize: '12px', width: '300px' }}
              >
                Choose what actions Quibble can perform during interactions such
                as Call Transfer, accessing an API or sending SMS
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                width: '50%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  minHeight: '32px',
                  bgcolor: 'white',
                  p: 1,
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  width: '100%',
                }}
              >
                {personalization.data?.selectedActions?.map(
                  (action: selectedActionData) => (
                    <Chip
                      key={action}
                      label={action}
                      onDelete={() => handleActionDelete(action)}
                      size="small"
                      sx={{
                        height: '22px',
                        fontSize: '12px',
                        fontWeight: 400,
                        color: 'custom.textBlue',
                        bgcolor: 'white',
                      }}
                    />
                  )
                )}
              </Box>
              <Select
                value={personalization.data?.selectedActions?.[0] || ''}
                onChange={e => {
                  handleActionAdd(e.target.value as selectedActionData);
                }}
                displayEmpty
                size="small"
                sx={{
                  width: '35%',
                  bgcolor: '#F8F9FC',
                  height: '32px',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    padding: '4px 14px',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: '1px solid #DCE0E7',
                    background: '#F8F9FC',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    color: theme.palette.lightGray.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Add Action
                </MenuItem>
                <MenuItem value="api">API</MenuItem>
                <MenuItem value="transfer">Transfer Call</MenuItem>
                <MenuItem value="sms">Send SMS</MenuItem>
                <MenuItem value="email">Send Email</MenuItem>
                <MenuItem value="schedule">Scheduling</MenuItem>
                <MenuItem value="take-message">Take Message</MenuItem>
              </Select>
            </Box>
          </Box>
          {personalization.data?.selectedActions?.map(
            (action: selectedActionData) => (
              <Box key={action}>
                {action === 'schedule' && <Scheduling />}
                {action === 'sms' && <SendSMS />}
                {action === 'email' && <SendEmail />}
                {action === 'transfer' && <TransferCall />}
                {action === 'take-message' && <TakingMessage />}
                {action === 'api' && <API />}
              </Box>
            )
          )}
        </Box>
      </Box>
      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 'auto',
          p: 1,
          borderTop: '1px solid',
          borderColor: 'lightGray.main',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Button
          variant="outlined"
          sx={{
            height: '34px',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '8px',
            color: 'custom.textBlue',
            backgroundColor: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
          startIcon={<CaretLeft size={16} />}
          onClick={handlePrev}
        >
          Prev
        </Button>
        <Button
          variant="contained"
          sx={{
            height: '34px',
            borderRadius: '8px',
            backgroundColor: 'primary.main',
            color: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
          endIcon={<CaretRight size={16} />}
          onClick={handleNext}
          disabled={isPending}
        >
          {isPending ? 'Saving...' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default Personalization;
